
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { FeaturedTrack } from '@/hooks/useFeaturedTracks';
import { extractSpotifyTrackId } from '@/lib/spotify';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2 } from 'lucide-react';

interface TrackItemProps {
  track: FeaturedTrack;
  onDelete: (id: string) => void;
}

const TrackItem = ({ track, onDelete }: TrackItemProps) => {
  const spotifyTrackId = track.spotify_url ? extractSpotifyTrackId(track.spotify_url) : null;
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(track.id);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <div className="p-4 mb-4 border rounded-md bg-card hover:border-primary transition-colors">      
      <div>
        {spotifyTrackId ? (
          <>
            {isLoading && (
              <div className="w-full h-[80px] bg-secondary/50 rounded-md flex items-center justify-center">
                <Skeleton className="w-full h-full absolute" />
              </div>
            )}
            <iframe 
              src={`https://open.spotify.com/embed/track/${spotifyTrackId}?utm_source=generator`}
              width="100%" 
              height="80" 
              frameBorder="0" 
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
              loading="lazy"
              className={`w-full rounded-md ${isLoading ? 'opacity-0' : 'opacity-100'}`}
              onLoad={handleIframeLoad}
              style={{ transition: "opacity 0.3s ease-in-out" }}
            />
          </>
        ) : (
          <div className="h-20 bg-secondary/30 rounded-md flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Invalid Spotify URL</p>
          </div>
        )}
      </div>
      
      <div className="mt-3 flex justify-end">
        <Button 
          variant="destructive" 
          onClick={handleDeleteClick}
          className="w-full"
          size="sm"
        >
          Delete Track
        </Button>
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Track</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this track? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TrackItem;
